import { useState } from "react";
import { useCounter, useInterval } from "usehooks-ts";
import * as Sentry from "@sentry/react";

type ClientF<Out> = () => Promise<Out>;

type PollerOutput = {
  isPolling: boolean;
  stopPolling: () => void;
};

type DelayedPollerOutput<Out> = PollerOutput & {
  startPolling: (clientF: ClientF<Out>) => void;
};

export function useDelayedPoller<Out>(
  setState: (out: Out) => void,
  rateMs: number,
): DelayedPollerOutput<Out> {
  return useBasePoller(setState, rateMs, null);
}

export function usePoller<Out>(
  clientF: ClientF<Out>,
  setState: (out: Out) => void,
  rateMs: number,
): PollerOutput {
  const { startPolling, ...rest } = useBasePoller(setState, rateMs, clientF);
  return rest;
}

const retryForMs = 10000;
function useBasePoller<Out>(
  setState: (out: Out) => void,
  rateMs: number,
  initialize: ClientF<Out> | null,
): DelayedPollerOutput<Out> {
  const retryForN = retryForMs / rateMs;

  const {
    count: consecutiveFailures,
    increment: incFailures,
    reset: resetFailures,
  } = useCounter(0);

  // useState cannot store functions due to 'lazy initializaton'
  //   it will execute an () => Promise<A> and store a 'Promise<A>' as initial value.
  // we wrap explicitly into an object {f:..} to prevent this behavior
  type ClientFThunk<Out> = { f: ClientF<Out> };
  function toThunk(f: ClientF<Out>): ClientFThunk<Out> {
    return { f: f };
  }

  const initializeThunk = initialize === null ? null : toThunk(initialize);
  const [clientF, setClientF] = useState<ClientFThunk<Out> | null>(
    initializeThunk,
  );

  const isPolling = clientF !== null;

  const stopPolling = () => {
    if (isPolling) {
      Sentry.addBreadcrumb({
        type: "default",
        level: "info",
        category: `${useBasePoller.name}-state`,
        message: `Stop polling`,
      });
      resetFailures();
      setClientF(null);
    }
  };

  useInterval(
    () => {
      if (clientF === null) return;
      if (consecutiveFailures >= retryForN) return stopPolling();

      clientF
        .f()
        .then((state) => {
          setState(state);
          resetFailures();
        })
        .catch(() => {
          incFailures();
        });
    },
    isPolling ? rateMs : null,
  );

  return {
    isPolling: isPolling,
    startPolling: (f) => {
      setClientF(toThunk(f));
    },
    stopPolling: stopPolling,
  };
}
